import { DateObj } from '../types/formBehavior.types'

export type DurationType = 'DAYS' | 'MONTHS' | 'YEARS'

export type DurationKind = 'DAYS' | 'MONTHS' | 'YEARS' | 'MANUAL' | 'INDEFINITE'

const DURATION_KINDS = new Set<string>([
  'DAYS',
  'MONTHS',
  'YEARS',
  'MANUAL',
  'INDEFINITE',
])

const CALCULABLE_DURATION_KINDS = new Set<string>(['DAYS', 'MONTHS', 'YEARS'])

const EXPIRATION_DISABLED_KINDS = new Set<string>([
  'DAYS',
  'MONTHS',
  'YEARS',
  'INDEFINITE',
])

const extractDurationKindString = (raw: unknown): string | null => {
  if (typeof raw === 'string' && raw.trim()) {
    return raw.trim().toUpperCase()
  }
  if (raw != null && typeof raw === 'object') {
    const name = (raw as { name?: unknown }).name
    if (typeof name === 'string' && name.trim()) {
      return name.trim().toUpperCase()
    }
    const value = (raw as { value?: unknown }).value
    if (typeof value === 'string' && value.trim()) {
      return value.trim().toUpperCase()
    }
  }
  return null
}

export const parseDurationKind = (raw: unknown): DurationKind | null => {
  const kind = extractDurationKindString(raw)
  if (!kind || !DURATION_KINDS.has(kind)) return null
  return kind as DurationKind
}

export const isCalculableDurationKind = (kind: DurationKind | null): boolean =>
  !!kind && CALCULABLE_DURATION_KINDS.has(kind)

export const isExpirationFieldDisabledKind = (
  kind: DurationKind | null
): boolean => !!kind && EXPIRATION_DISABLED_KINDS.has(kind)

const parseDate = (dateStr: string): DateObj | null => {
  const match = dateStr.match(/^(\d{4})-(\d{2})-(\d{2})$/)
  if (!match) return null

  const [, y, m, d] = match.map(Number)
  const date = new Date(y, m - 1, d)

  return date.getFullYear() === y &&
    date.getMonth() === m - 1 &&
    date.getDate() === d
    ? { year: y, month: m - 1, day: d }
    : null
}

const formatDate = (obj: DateObj): string => {
  const pad = (n: number) => (n < 10 ? '0' : '') + n
  return `${obj.year}-${pad(obj.month + 1)}-${pad(obj.day)}`
}

const addDays = (date: DateObj, days: number): DateObj => {
  const jsDate = new Date(date.year, date.month, date.day)
  jsDate.setDate(jsDate.getDate() + days)
  return {
    year: jsDate.getFullYear(),
    month: jsDate.getMonth(),
    day: jsDate.getDate(),
  }
}

const addMonths = (date: DateObj, months: number): DateObj => {
  let { year, month } = date
  const day = date.day
  month += months

  while (month > 11) {
    month -= 12
    year += 1
  }
  while (month < 0) {
    month += 12
    year -= 1
  }

  return { year, month, day }
}

const addYears = (date: DateObj, years: number): DateObj => {
  return {
    year: date.year + years,
    month: date.month,
    day: date.day,
  }
}

export const normalizeDurationType = (raw: unknown): DurationType | null => {
  const kind = parseDurationKind(raw)
  if (!isCalculableDurationKind(kind)) return null
  return kind as DurationType
}

export const normalizeDurationValue = (raw: unknown): number | null => {
  const num = Number(raw)
  if (!Number.isFinite(num) || num <= 0) return null
  return num
}

export const normalizeStartDate = (raw: unknown): string | null => {
  if (typeof raw !== 'string' || !raw.trim()) return null
  return parseDate(raw.trim()) ? raw.trim() : null
}

export const calculateExpirationDate = (
  durationType: DurationType,
  value: number,
  startDate: string
): string => {
  if (value <= 0) throw new Error('Value must be positive')
  if (typeof startDate !== 'string') {
    throw new Error('Expected date string YYYY-MM-DD')
  }

  const parsed = parseDate(startDate)
  if (!parsed) throw new Error(`Invalid date format: ${startDate}`)

  let result: DateObj

  if (durationType === 'DAYS') {
    result = addDays(parsed, value)
  } else if (durationType === 'MONTHS') {
    result = addMonths(parsed, value)
  } else if (durationType === 'YEARS') {
    result = addYears(parsed, value)
  } else {
    throw new Error('Invalid durationType')
  }

  const maxDay = new Date(result.year, result.month + 1, 0).getDate()
  result.day = Math.min(result.day, maxDay)

  return formatDate(result)
}
